import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import PageSectionNav from '../../components/feature/PageSectionNav';

const PRESET_AMOUNTS = [25, 50, 100, 200];
const MONTHLY_PRESETS = [10, 25, 50, 100];

export default function DonatePage() {
  const { t } = useTranslation();
  const [selectedAmount, setSelectedAmount] = useState<number | 'custom'>(50);
  const [customAmount, setCustomAmount] = useState('');
  const [isMonthly, setIsMonthly] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);

  const activePresets = isMonthly ? MONTHLY_PRESETS : PRESET_AMOUNTS;

  // PayPal business account that receives the donations
  const PAYPAL_BUSINESS = 'treasurer@smpcvi.ca';

  const handleAmountClick = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  // Load the official PayPal Donate SDK once
  useEffect(() => {
    const w = window as unknown as { PayPal?: { Donation?: unknown } };
    if (w.PayPal && w.PayPal.Donation) {
      setSdkReady(true);
      return;
    }
    const existing = document.querySelector('script[data-paypal-donate]') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => setSdkReady(true));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://www.paypalobjects.com/donate/sdk/donate-sdk.js';
    script.setAttribute('charset', 'UTF-8');
    script.setAttribute('data-paypal-donate', 'true');
    script.onload = () => setSdkReady(true);
    document.body.appendChild(script);
  }, []);

  // Render (and re-render) the official PayPal Donate button whenever the amount changes
  useEffect(() => {
    if (!sdkReady) return;
    const container = document.getElementById('paypal-donate-button');
    if (!container) return;
    const w = window as unknown as {
      PayPal?: { Donation?: { Button: (cfg: Record<string, unknown>) => { render: (sel: string) => void } } };
    };
    const Donation = w.PayPal && w.PayPal.Donation;
    if (!Donation) return;
    container.innerHTML = '';
    const amountValue = selectedAmount === 'custom' ? customAmount : String(selectedAmount);
    const cfg: Record<string, unknown> = {
      env: 'production',
      business: PAYPAL_BUSINESS,
      item_name: isMonthly
        ? 'Monthly Donation — Saint Mina & Pope Cyril VI Church'
        : 'Donation — Saint Mina & Pope Cyril VI Church',
      currency_code: 'CAD',
      onComplete: () => { /* PayPal shows its own confirmation to the donor */ },
    };
    if (amountValue && Number(amountValue) > 0) {
      cfg.amount = amountValue;
    }
    Donation.Button(cfg).render('#paypal-donate-button');
  }, [sdkReady, selectedAmount, customAmount, isMonthly]);

  return (
    <PageLayout hasHero>
      <PageHero
        title={t('donatePage.title')}
        subtitle={t('donatePage.subtitle')}
        backgroundImage="/assets/readdy/eec338d751d21eb4402794d962a4342a-de1dd4d85d67.png"
      />

      <PageSectionNav
        sections={[
          { id: 'online-giving', label: 'Online Giving' },
          { id: 'other-ways', label: 'Other Ways to Give' },
        ]}
      />

      <section id="online-giving" className="py-20 bg-stone-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-6">
              {t('donatePage.supportMission')} <span className="italic text-[#3d1a1e]">{t('donatePage.mission')}</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-4">
              {t('donatePage.donateDesc')}
            </p>
            <p className="text-sm text-gray-500 mb-10">
              {t('donatePage.taxDeductible')}
            </p>

            {/* Registered Charity Badge */}
            <div className="inline-flex items-center gap-3 bg-white border border-stone-200 rounded-full px-5 py-2.5 mb-8 shadow-sm">
              <div className="w-8 h-8 flex items-center justify-center bg-[#3d1a1e] rounded-full">
                <i className="ri-award-line text-sm text-white"></i>
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-gray-900">{t('donatePage.registeredCharity')}</p>
                <p className="text-xs text-gray-500">{t('donatePage.charityNumber')}</p>
              </div>
            </div>

            {/* Tax Receipt Note */}
            <div className="flex items-center justify-center gap-2 text-sm text-[#3d1a1e] font-semibold mb-10">
              <i className="ri-file-list-3-line text-lg"></i>
              <span>{t('donatePage.taxReceiptsWillBeIssued')}</span>
            </div>

            {/* One-time / Monthly Toggle */}
            <div className="max-w-xs mx-auto mb-8">
              <div className="flex bg-white rounded-full p-1 border border-gray-200">
                <button
                  onClick={() => setIsMonthly(false)}
                  className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    !isMonthly
                      ? 'bg-[#3d1a1e] text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('donatePage.oneTime')}
                </button>
                <button
                  onClick={() => setIsMonthly(true)}
                  className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    isMonthly
                      ? 'bg-[#3d1a1e] text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('donatePage.monthly')}
                </button>
              </div>
            </div>

            {/* Recurring Highlight */}
            {isMonthly && (
              <div className="max-w-lg mx-auto mb-8 bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-4 text-left">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-amber-100 rounded-xl">
                  <i className="ri-heart-pulse-line text-xl text-amber-700"></i>
                </div>
                <div>
                  <p className="font-bold text-amber-900 text-sm mb-1">{t('donatePage.whyMonthly')}</p>
                  <p className="text-sm text-amber-800 leading-relaxed">{t('donatePage.monthlyImpact')}</p>
                </div>
              </div>
            )}

            {/* Donation Amount Selector */}
            <div className="max-w-md mx-auto mb-8">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-4">
                {isMonthly ? t('donatePage.monthlyGift') : t('donatePage.selectAmount')}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
                {activePresets.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handleAmountClick(amount)}
                    className={`py-3 rounded-xl text-sm font-bold border-2 transition-all cursor-pointer whitespace-nowrap ${
                      selectedAmount === amount
                        ? 'bg-[#3d1a1e] text-white border-[#3d1a1e]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-[#3d1a1e] hover:text-[#3d1a1e]'
                    }`}
                  >
                    ${amount}
                    {isMonthly && <span className="block text-[10px] font-normal opacity-70">/mo</span>}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setSelectedAmount('custom')}
                className={`w-full py-3 rounded-xl text-sm font-bold border-2 transition-all cursor-pointer whitespace-nowrap ${
                  selectedAmount === 'custom'
                    ? 'border-[#3d1a1e] bg-stone-50'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-[#3d1a1e] hover:text-[#3d1a1e]'
                }`}
              >
                {selectedAmount === 'custom' ? (
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-gray-400">$</span>
                    <input
                      type="number"
                      min="1"
                      placeholder={t('donatePage.enterAmount')}
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-32 bg-transparent text-center text-[#3d1a1e] font-bold outline-none placeholder-gray-400 text-sm"
                    />
                  </div>
                ) : (
                  <span className="text-gray-700">{t('donatePage.customAmount')}</span>
                )}
              </button>
            </div>

            <div className="flex flex-col items-center justify-center gap-3 mb-8">
              <div className="text-sm font-semibold text-gray-700">
                {selectedAmount === 'custom'
                  ? (customAmount ? `$${customAmount} CAD${isMonthly ? ' / month' : ''}` : 'Enter an amount above')
                  : `$${selectedAmount} CAD${isMonthly ? ' / month' : ''}`}
              </div>
              {/* Official PayPal Donate button renders here (opens PayPal's secure popup) */}
              <div id="paypal-donate-button" className="min-h-[50px] flex items-center justify-center"></div>
              {!sdkReady && (
                <p className="text-sm text-gray-400">
                  <i className="ri-loader-4-line animate-spin mr-1"></i>
                  Loading secure PayPal…
                </p>
              )}
            </div>

            <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-lg text-green-600"></i>
                <span>{t('donatePage.securePayment')}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-lock-line text-lg text-green-600"></i>
                <span>{t('donatePage.sslEncrypted')}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-file-text-line text-lg text-green-600"></i>
                <span>{t('donatePage.taxReceipt')}</span>
              </div>
            </div>
          </div>

          {/* Bible Verse */}
          <div className="max-w-2xl mx-auto mt-16 mb-4 text-center">
            <div className="relative px-10 py-10 bg-[#3d1a1e]/5 rounded-3xl border border-[#3d1a1e]/10">
              <i className="ri-double-quotes-l text-5xl text-[#3d1a1e]/20 absolute top-4 left-6 leading-none"></i>
              <p className="text-xl font-serif italic text-gray-800 leading-relaxed relative z-10">
                {t('donatePage.bibleVerse')}
              </p>
              <p className="mt-4 text-sm font-semibold text-[#3d1a1e] tracking-widest uppercase">{t('donatePage.bibleVerseRef')}</p>
              <i className="ri-double-quotes-r text-5xl text-[#3d1a1e]/20 absolute bottom-4 right-6 leading-none"></i>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-10">
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-building-line text-3xl text-gold-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{t('donatePage.ministryPrograms')}</h4>
              <p className="text-sm text-gray-600">{t('donatePage.ministryProgramsDesc')}</p>
            </div>

            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-hand-heart-line text-3xl text-gold-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{t('donatePage.communityOutreach')}</h4>
              <p className="text-sm text-gray-600">{t('donatePage.communityOutreachDesc')}</p>
            </div>

            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-global-line text-3xl text-gold-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{t('donatePage.missionWork')}</h4>
              <p className="text-sm text-gray-600">{t('donatePage.missionWorkDesc')}</p>
            </div>
          </div>

          {/* Other Ways to Give */}
          <div id="other-ways" className="mt-16 bg-white rounded-3xl p-10">
            <h3 className="text-2xl font-serif font-bold text-gray-900 mb-8 text-center">{t('donatePage.otherWaysToGive')}</h3>

            {/* PayPal + Interac side by side as prominent cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {/* PayPal Card */}
              <div className="border border-gray-200 rounded-2xl p-6 hover:border-[#3d1a1e]/30 transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#003087]/10 rounded-xl flex-shrink-0">
                    <i className="ri-paypal-line text-2xl text-[#003087]"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1">{t('donatePage.giveWithPayPal')}</h4>
                    <p className="text-sm text-gray-500 mb-4">{t('donatePage.giveWithPayPalDesc')}</p>
                    <button
                      onClick={() => document.getElementById('online-giving')?.scrollIntoView({ behavior: 'smooth' })}
                      className="w-full py-3 bg-[#003087] text-white text-sm font-semibold rounded-xl hover:bg-[#002a6e] transition-all cursor-pointer whitespace-nowrap"
                    >
                      {t('donatePage.openPayPal')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Interac Card */}
              <div className="border border-gray-200 rounded-2xl p-6 hover:border-[#3d1a1e]/30 transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#3d1a1e]/10 rounded-xl flex-shrink-0">
                    <i className="ri-bank-card-line text-2xl text-[#3d1a1e]"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1">{t('donatePage.interacTransfer')}</h4>
                    <p className="text-sm text-gray-500 mb-4">{t('donatePage.interacDesc')}</p>
                    <div className="text-sm text-gray-700 bg-stone-50 rounded-xl p-4 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <i className="ri-mail-send-line text-gray-400"></i>
                        <span><span className="font-semibold">{t('donatePage.interacEmail')}:</span> treasurer@smpcvi.ca</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-question-line text-gray-400"></i>
                        <span><span className="font-semibold">{t('donatePage.interacQuestion')}:</span> What is the church name?</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-key-line text-gray-400"></i>
                        <span><span className="font-semibold">{t('donatePage.interacAnswer')}:</span> SMPCVI</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">{t('donatePage.interacTaxNote')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-stone-100 mb-10"></div>

            {/* Mail a Check + In-Kind Donations side by side */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-xl flex-shrink-0">
                  <i className="ri-mail-line text-xl text-[#3d1a1e]"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">{t('donatePage.mailCheck')}</h4>
                  <p className="text-sm text-gray-600">{t('donatePage.mailCheckDesc')}</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-xl flex-shrink-0">
                  <i className="ri-gift-line text-xl text-[#3d1a1e]"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">{t('donatePage.inKindDonations')}</h4>
                  <p className="text-sm text-gray-600">{t('donatePage.inKindDonationsDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </PageLayout>
  );
}
